/**
 * Global signal for "the session died while the tab was open".
 *
 * The cookie can expire with the app fully rendered: `['auth-bootstrap']` is
 * `staleTime: Infinity` and `refetchOnWindowFocus` is off, so nothing re-checks
 * the session and every request just 401s in the background. Before this
 * signal the person kept clicking on a page that looked alive, waited out the
 * retries and only found out on a reload, back at the sign-in screen.
 *
 * It lives outside React because the emitter is the axios interceptor, which
 * has no access to the tree. `SessionExpiredDialog` subscribes through
 * `useSyncExternalStore`.
 */

let expired = false;
let authenticated = false;
const listeners = new Set<() => void>();

/**
 * Mirrors `useAuth().isAuthenticated`. A 401 only means "expired session" if
 * we had a session to begin with — on a public page (answering a test or a
 * survey by link) it means the link itself is invalid.
 */
export const markSessionAuthenticated = (isAuthenticated: boolean) => {
    authenticated = isAuthenticated;
};

export const isSessionExpired = () => expired;

/**
 * Idempotent: a dead session usually 401s several requests at once, and the
 * dialog must open on the first one and stay put.
 */
export const notifySessionExpired = () => {
    if (expired || !authenticated) return;

    expired = true;
    listeners.forEach((listener) => listener());
};

export const subscribeToSessionExpiry = (listener: () => void) => {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
};

/** Test-only reset — the module state outlives a single test otherwise. */
export const resetSessionExpiryForTests = () => {
    expired = false;
    authenticated = false;
    listeners.clear();
};
